import React, { useEffect, useMemo, useState } from "react";
import { collection, query, where, onSnapshot, doc, updateDoc, writeBatch } from "firebase/firestore";
import { firestore } from "../shared/FirebaseConfig";
import { useUser } from "../shared/UserContext";
import { studentGrade, hasGraduated } from "../shared/gradeModel";
import { teacherDisplayName } from "./signoutData";
import { activeClassOf } from "../shared/activeClass";
import { useNavigate } from "react-router-dom";
import { byLastName } from "../shared/nameSort";

// "Last, First" for display, falling back to studentName. The surname is the
// full remainder after the first name ("Bryan Balmaceda Ortega" → "Balmaceda
// Ortega, Bryan"), so compound names group under the right letter.
const lastFirst = (s) => {
  const ln = s.lastName || (s.studentName || "").split(" ").slice(1).join(" ") || "";
  const fn = s.firstName || (s.studentName || "").split(" ")[0] || "";
  return `${ln}${ln && fn ? ", " : ""}${fn}`.trim() || s.studentName || "Student";
};

// Admin-only school-wide roster: every class + its students, with a one-click
// "Move to…" that reassigns a student's homeroom (student-doc write only — the
// rules already allow a same-school admin, and attendance/emergency read the
// student's enrolledClasses/homeroomClassId).
export default function SchoolRoster() {
  const { schoolId, role, loading, gradeSpan, schoolYearEnd } = useUser();
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState({}); // uid -> name
  const [searchTerm, setSearchTerm] = useState("");
  const [expanded, setExpanded] = useState(() => new Set()); // expanded class ids (default: all collapsed)
  const [showArchived, setShowArchived] = useState(false);
  const [selected, setSelected] = useState(() => new Set()); // selected student ids (bulk)

  useEffect(() => {
    if (!loading && role !== "admin") navigate("/dashboard");
  }, [role, loading, navigate]);

  useEffect(() => {
    if (!schoolId) return;
    const unsubs = [
      onSnapshot(query(collection(firestore, "classes"), where("schoolId", "==", schoolId)),
        snap => setClasses(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
        err => console.error("[SchoolRoster] classes:", err)),
      onSnapshot(query(collection(firestore, "students"), where("schoolId", "==", schoolId)),
        snap => setStudents(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
        err => console.error("[SchoolRoster] students:", err)),
      onSnapshot(query(collection(firestore, "users"), where("schoolId", "==", schoolId)),
        snap => { const m = {}; snap.docs.forEach(d => { m[d.id] = teacherDisplayName(d.data()); }); setTeachers(m); },
        err => console.error("[SchoolRoster] users:", err)),
    ];
    return () => unsubs.forEach(fn => fn());
  }, [schoolId]);

  const classMap = useMemo(() => {
    const m = {};
    classes.forEach(c => { m[c.id] = { className: c.className || "Class", teacherId: c.teacherId, isArchived: c.isArchived === true, isHomeroom: c.isHomeroom === true, isSpecials: c.classType === "specials" }; });
    return m;
  }, [classes]);

  const activeClassIds = useMemo(() => new Set(classes.filter(c => !c.isArchived).map(c => c.id)), [classes]);
  const homeroomClassIds = useMemo(() => new Set(classes.filter(c => c.isHomeroom && !c.isArchived).map(c => c.id)), [classes]);
  const specialsClassIds = useMemo(() => new Set(classes.filter(c => c.classType === "specials").map(c => c.id)), [classes]);

  // A student's "home" class: their homeroom if active, else a homeroom-flagged
  // enrolled class, else their first active enrolled class, else none (→ Unassigned).
  // Shared helper so the roster groups kids the same way the Emergency board does.
  const homeOf = (s) => activeClassOf(s, activeClassIds, homeroomClassIds, specialsClassIds);

  // Active classes: homeroom-flagged first, then A–Z.
  const activeClasses = useMemo(() =>
    classes.filter(c => !c.isArchived).sort((a, b) => {
      if (!!b.isHomeroom !== !!a.isHomeroom) return a.isHomeroom ? -1 : 1;
      return (a.className || "").localeCompare(b.className || "");
    }), [classes]);

  const groups = useMemo(() => {
    const g = {};
    students.forEach(s => {
      const h = homeOf(s) || "unassigned";
      (g[h] = g[h] || []).push(s);
    });
    Object.values(g).forEach(list => list.sort(byLastName));
    return g;
  }, [students, activeClassIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const teacherFor = (cid) => teachers[classMap[cid]?.teacherId] || "—";

  const moveStudent = async (s, targetClassId) => {
    const oldHome = homeOf(s);
    if (targetClassId === oldHome) return;
    const next = [...new Set(
      (s.enrolledClasses || []).filter(c => c !== oldHome).concat(targetClassId ? [targetClassId] : [])
    )];
    try {
      await updateDoc(doc(firestore, "students", s.id), {
        homeroomClassId: targetClassId || null,
        enrolledClasses: next,
      });
    } catch (err) {
      console.error("Move failed:", err);
    }
  };

  const toggleCollapse = (cid) =>
    setExpanded(prev => { const n = new Set(prev); n.has(cid) ? n.delete(cid) : n.add(cid); return n; });

  // Jump-to-class quick nav: expand the target section, then scroll it into view.
  const jumpTo = (cid) => {
    setExpanded(prev => (prev.has(cid) ? prev : new Set(prev).add(cid)));
    requestAnimationFrame(() =>
      document.getElementById(`roster-sec-${cid}`)?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };

  // Archive/un-archive a class (admin). isArchived-only write — rules allow a
  // same-school admin. Reversible; archived classes drop out of active views but
  // keep their history. Their students fall to "Unassigned" until re-homed.
  const archiveClass = async (c) => {
    if (!window.confirm(`Archive "${c.className}"? It drops out of attendance, sign-out, and emergencies (reversible). Its students become Unassigned until you move them.`)) return;
    try { await updateDoc(doc(firestore, "classes", c.id), { isArchived: true }); }
    catch (err) { console.error("Archive failed:", err); }
  };
  const unarchiveClass = async (c) => {
    try { await updateDoc(doc(firestore, "classes", c.id), { isArchived: false }); }
    catch (err) { console.error("Un-archive failed:", err); }
  };

  // Graduate / leaving school: detach the student from the school (one write removes
  // them from every school-scoped view — roster, directory, sign-out, attendance,
  // emergency). The doc + its history are kept; a record of where/when is stamped.
  // Rules allow it (a same-school admin may update the student while it's still here).
  // Graduate a whole class at once (e.g. a top-grade cohort leaving). Detaches every
  // student in the class in one batch; the now-empty class can be archived separately.
  const graduateClass = async (c, list) => {
    if (!list.length) return;
    if (!window.confirm(`Graduate all ${list.length} student${list.length !== 1 ? "s" : ""} in "${c.className}"? They leave the school (records are kept) and the now-empty class is archived. This can't be undone here.`)) return;
    try {
      const batch = writeBatch(firestore);
      list.forEach(s => batch.update(doc(firestore, "students", s.id), {
        schoolId: "graduated",
        graduatedFromSchoolId: schoolId,
        graduatedAt: new Date().toISOString(),
        enrolledClasses: [],
        homeroomClassId: null,
      }));
      // Archive the now-empty class so it drops off the active Classes screen /
      // pickers (a graduated class shouldn't linger as "active" with 0 students).
      // Admins may toggle ONLY isArchived per the classes update rule.
      batch.update(doc(firestore, "classes", c.id), { isArchived: true });
      await batch.commit();
    } catch (err) {
      console.error("Graduate class failed:", err);
    }
  };

  // ── Bulk selection ───────────────────────────────────────────────────────
  const toggleSelect = (id) =>
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const setManySelected = (ids, on) =>
    setSelected(prev => { const n = new Set(prev); ids.forEach(id => (on ? n.add(id) : n.delete(id))); return n; });
  const clearSelected = () => setSelected(new Set());

  const bulkMove = async (targetClassId) => {
    const sel = students.filter(s => selected.has(s.id));
    if (!sel.length) return;
    try {
      const batch = writeBatch(firestore);
      sel.forEach(s => {
        const oldHome = homeOf(s);
        const next = [...new Set((s.enrolledClasses || []).filter(c => c !== oldHome).concat(targetClassId ? [targetClassId] : []))];
        batch.update(doc(firestore, "students", s.id), { homeroomClassId: targetClassId || null, enrolledClasses: next });
      });
      await batch.commit();
      clearSelected();
    } catch (err) { console.error("Bulk move failed:", err); }
  };
  const bulkGraduate = async () => {
    const sel = students.filter(s => selected.has(s.id));
    if (!sel.length) return;
    if (!window.confirm(`Graduate ${sel.length} selected student${sel.length !== 1 ? "s" : ""}? They leave the school (records are kept). This can't be undone here.`)) return;
    try {
      const batch = writeBatch(firestore);
      sel.forEach(s => batch.update(doc(firestore, "students", s.id), {
        schoolId: "graduated", graduatedFromSchoolId: schoolId, graduatedAt: new Date().toISOString(),
        enrolledClasses: [], homeroomClassId: null,
      }));
      await batch.commit();
      clearSelected();
    } catch (err) { console.error("Bulk graduate failed:", err); }
  };

  const graduateStudent = async (s) => {
    if (!window.confirm(`Graduate ${lastFirst(s)}? They'll be removed from the school roster (records are kept). This can't be undone here.`)) return;
    try {
      await updateDoc(doc(firestore, "students", s.id), {
        schoolId: "graduated",
        graduatedFromSchoolId: schoolId,
        graduatedAt: new Date().toISOString(),
        enrolledClasses: [],
        homeroomClassId: null,
      });
    } catch (err) {
      console.error("Graduate failed:", err);
    }
  };

  // "Move to…" select that resets to its placeholder after a choice.
  const MoveControl = ({ s }) => {
    const cur = homeOf(s);
    return (
      <select
        value=""
        onChange={(e) => {
          const v = e.target.value;
          if (v === "__graduate__") graduateStudent(s);
          else if (v) moveStudent(s, v === "__none__" ? null : v);
        }}
        style={{ width: "auto", minWidth: 150 }}
      >
        <option value="">Move to…</option>
        {activeClasses.filter(c => c.id !== cur).map(c => (
          <option key={c.id} value={c.id}>{c.className}</option>
        ))}
        {cur && <option value="__none__">— Remove from class —</option>}
        <option value="__graduate__">— Graduate (leaving school) —</option>
      </select>
    );
  };

  const StudentRow = ({ s, showClass }) => {
    const grade = studentGrade(s, schoolYearEnd);
    // Aged out of the school's top grade → flag as a graduate candidate (the admin
    // still clicks Graduate to actually detach). Old students (no gradYear) never flag.
    const graduating = gradeSpan?.high && hasGraduated(s.gradYear, gradeSpan.high, schoolYearEnd);
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "8px 0", borderTop: "1px solid #f0f0f0", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <input
            type="checkbox"
            checked={selected.has(s.id)}
            onChange={() => toggleSelect(s.id)}
            style={{ width: 16, height: 16, cursor: "pointer", flexShrink: 0 }}
          />
          <div>
            <span style={{ fontWeight: 600 }}>{lastFirst(s)}</span>
            {graduating && (
              <span title="Aged out of the school's top grade — ready to graduate" style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: "#b45309", background: "#fef3c7", borderRadius: 6, padding: "1px 6px", verticalAlign: "middle" }}>
                🎓 Graduating
              </span>
            )}
            <span style={{ color: "var(--color-secondary)", fontSize: 13, marginLeft: 8 }}>
              {grade ? `Gr ${grade}` : ""}{s.studentId ? ` · ${s.studentId}` : ""}
              {showClass && (() => { const h = homeOf(s); return ` · ${h ? classMap[h]?.className : "Unassigned"}`; })()}
            </span>
          </div>
        </div>
        <MoveControl s={s} />
      </div>
    );
  };

  if (loading) return <div>Loading…</div>;

  const term = searchTerm.trim().toLowerCase();
  const matches = term
    ? students
        .filter(s => (s.studentName || "").toLowerCase().includes(term) || (s.studentId || "").toLowerCase().includes(term))
        .sort(byLastName)
    : [];
  const unassigned = groups.unassigned || [];
  const archivedClasses = classes
    .filter(c => c.isArchived)
    .sort((a, b) => (a.className || "").localeCompare(b.className || ""));

  return (
    <div className="add-student-form-container">
      <h1 style={{ marginBottom: 6 }}>School Roster</h1>
      <p style={{ color: "var(--color-secondary)", margin: "0 0 16px", maxWidth: 640 }}>
        Every class and who's in it. Use <strong>Move to…</strong> to reassign a student's
        homeroom — it updates their attendance, sign-out, and emergency grouping.
      </p>

      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search students by name or ID…"
          style={{ flex: "1 1 260px", maxWidth: 420, margin: 0 }}
        />
        {!term && activeClasses.length > 0 && (() => {
          const allOpen = activeClasses.every(c => expanded.has(c.id));
          return (
            <button
              className="btn-secondary"
              style={{ whiteSpace: "nowrap" }}
              onClick={() => setExpanded(allOpen ? new Set() : new Set(activeClasses.map(c => c.id)))}
            >
              {allOpen ? "Collapse all" : "Expand all"}
            </button>
          );
        })()}
      </div>

      {!term && activeClasses.length > 1 && (
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <span style={{ fontSize: 13, color: "var(--color-secondary)" }}>Jump to:</span>
          {activeClasses.map(c => (
            <button key={c.id} className="btn-ghost" style={{ fontSize: 13, padding: "4px 10px" }} onClick={() => jumpTo(c.id)}>
              {c.className}
            </button>
          ))}
          {unassigned.length > 0 && (
            <button className="btn-ghost" style={{ fontSize: 13, padding: "4px 10px", color: "#b45309", borderColor: "#f3d39b" }} onClick={() => jumpTo("__unassigned__")}>
              Unassigned
            </button>
          )}
        </div>
      )}

      {selected.size > 0 && (
        <div style={{ position: "sticky", top: "calc(var(--nav-h) + 6px)", zIndex: 15, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 16, padding: "10px 14px", background: "#eef2ff", border: "1px solid #c7d2fe", borderRadius: "var(--radius-md)", boxShadow: "var(--shadow-md)" }}>
          <strong>{selected.size} selected</strong>
          <select
            value=""
            onChange={(e) => { const v = e.target.value; if (v) bulkMove(v === "__none__" ? null : v); e.target.value = ""; }}
            style={{ width: "auto", minWidth: 150, margin: 0 }}
          >
            <option value="">Move to…</option>
            {activeClasses.map(c => <option key={c.id} value={c.id}>{c.className}</option>)}
            <option value="__none__">— Remove from class —</option>
          </select>
          <button onClick={bulkGraduate} style={{ background: "#fff", color: "#b45309", border: "1px solid #f3d39b" }}>Graduate</button>
          <button className="btn-ghost" onClick={clearSelected}>Clear</button>
        </div>
      )}

      {term ? (
        <div className="card">
          <h2 style={{ marginBottom: 4 }}>Results <span style={{ fontSize: 14, fontWeight: 400, color: "var(--color-secondary)" }}>{matches.length}</span></h2>
          {matches.length === 0
            ? <p style={{ color: "var(--color-text-muted)" }}>No students match “{searchTerm}”.</p>
            : matches.map(s => <StudentRow key={s.id} s={s} showClass />)}
        </div>
      ) : (
        <>
          {activeClasses.map(c => {
            const list = groups[c.id] || [];
            const isCollapsed = !expanded.has(c.id);
            return (
              <div key={c.id} id={`roster-sec-${c.id}`} className="card" style={{ marginBottom: "var(--space-4)", scrollMarginTop: "calc(var(--nav-h) + 12px)" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
                    {list.length > 0 && (
                      <input
                        type="checkbox"
                        checked={list.every(s => selected.has(s.id))}
                        onChange={(e) => setManySelected(list.map(s => s.id), e.target.checked)}
                        style={{ width: 16, height: 16, cursor: "pointer", flexShrink: 0 }}
                        title="Select all in this class"
                      />
                    )}
                    <div onClick={() => toggleCollapse(c.id)} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", flex: 1, minWidth: 0 }}>
                      <span style={{ fontSize: 13, color: "var(--color-secondary)" }}>{isCollapsed ? "▸" : "▾"}</span>
                      <h2 style={{ margin: 0 }}>
                        {c.className}
                        {c.isHomeroom && <span className="badge" style={{ marginLeft: 8, background: "#e6efff", color: "#1d4ed8" }}>Homeroom</span>}
                      </h2>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                    <span style={{ color: "var(--color-secondary)", fontSize: 14 }}>
                      {teacherFor(c.id)} · {list.length} student{list.length !== 1 ? "s" : ""}
                    </span>
                    {list.length > 0 && (
                      <button className="btn-ghost" style={{ fontSize: 13, color: "#b45309", borderColor: "#f3d39b" }} onClick={() => graduateClass(c, list)}>Graduate class</button>
                    )}
                    <button className="btn-ghost" style={{ fontSize: 13 }} onClick={() => archiveClass(c)}>Archive</button>
                  </div>
                </div>
                {!isCollapsed && (list.length === 0
                  ? <p style={{ color: "var(--color-text-muted)", marginTop: 8 }}>No students.</p>
                  : <div style={{ marginTop: 8 }}>{list.map(s => <StudentRow key={s.id} s={s} />)}</div>)}
              </div>
            );
          })}

          {unassigned.length > 0 && (() => {
            const unassignedOpen = expanded.has("__unassigned__");
            return (
            <div id="roster-sec-__unassigned__" className="card" style={{ marginBottom: "var(--space-4)", borderColor: "#f0b4b1", background: "#fdf3f2", scrollMarginTop: "calc(var(--nav-h) + 12px)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={unassigned.every(s => selected.has(s.id))}
                  onChange={(e) => setManySelected(unassigned.map(s => s.id), e.target.checked)}
                  style={{ width: 16, height: 16, cursor: "pointer", flexShrink: 0 }}
                  title="Select all unassigned"
                />
                <div onClick={() => toggleCollapse("__unassigned__")} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: 13, color: "var(--color-secondary)" }}>{unassignedOpen ? "▾" : "▸"}</span>
                  <h2 style={{ margin: 0 }}>
                    Unassigned
                    <span style={{ fontSize: 14, fontWeight: 400, color: "var(--color-secondary)", marginLeft: 8 }}>
                      {unassigned.length} student{unassigned.length !== 1 ? "s" : ""} with no class
                    </span>
                  </h2>
                </div>
              </div>
              {unassignedOpen && <div style={{ marginTop: 8 }}>{unassigned.map(s => <StudentRow key={s.id} s={s} />)}</div>}
            </div>
            );
          })()}

          {activeClasses.length === 0 && unassigned.length === 0 && (
            <p style={{ color: "var(--color-text-muted)" }}>No classes or students yet.</p>
          )}

          {archivedClasses.length > 0 && (
            <div className="card" style={{ marginTop: "var(--space-5)", background: "var(--color-bg)" }}>
              <div onClick={() => setShowArchived(v => !v)} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <span style={{ fontSize: 13, color: "var(--color-secondary)" }}>{showArchived ? "▾" : "▸"}</span>
                <h2 style={{ margin: 0, fontSize: "1.1rem" }}>
                  Archived classes
                  <span style={{ fontSize: 14, fontWeight: 400, color: "var(--color-secondary)", marginLeft: 8 }}>{archivedClasses.length}</span>
                </h2>
              </div>
              {showArchived && archivedClasses.map(c => (
                <div key={c.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "8px 0", borderTop: "1px solid #f0f0f0", flexWrap: "wrap" }}>
                  <span>
                    <span style={{ fontWeight: 600 }}>{c.className}</span>
                    <span style={{ color: "var(--color-secondary)", fontSize: 13, marginLeft: 8 }}>{teacherFor(c.id)}</span>
                  </span>
                  <button className="btn-secondary" style={{ fontSize: 13 }} onClick={() => unarchiveClass(c)}>Un-archive</button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
